import {
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
  forwardRef,
} from 'react'
import { createRoot } from 'react-dom/client'
import { ConfigProvider, theme } from 'antd'
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion'
import type { User } from '../../../Contracts'
import ChatAvatar from '../../common/ChatAvatar'

interface MentionItem {
  id: string
  label: string
  avatarUrl: string
}

interface MentionListProps {
  items: MentionItem[]
  command: (item: MentionItem) => void
}

export interface MentionListRef {
  onKeyDown: (event: KeyboardEvent) => boolean
}

const MentionList = forwardRef<MentionListRef, MentionListProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const { token } = theme.useToken()

    useEffect(() => {
      setSelectedIndex(0)
    }, [items])

    const selectItem = useCallback(
      (index: number) => {
        const item = items[index]
        if (item) command(item)
      },
      [items, command],
    )

    useImperativeHandle(ref, () => ({
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((prev) => (prev <= 0 ? items.length - 1 : prev - 1))
          return true
        }
        if (event.key === 'ArrowDown') {
          setSelectedIndex((prev) => (prev >= items.length - 1 ? 0 : prev + 1))
          return true
        }
        if (event.key === 'Enter') {
          selectItem(selectedIndex)
          return true
        }
        if (event.key === 'Escape') {
          return true
        }
        return false
      },
    }))

    if (items.length === 0) return null

    return (
      <div
        className="chat-scroll"
        style={{
          background: token.colorBgElevated,
          borderRadius: 8,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorderSecondary}`,
          padding: 4,
          maxHeight: 260,
          overflowY: 'auto',
          minWidth: 200,
        }}
      >
        {items.map((item, index) => (
          <div
            key={item.id}
            onClick={() => selectItem(index)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 8px',
              borderRadius: 6,
              cursor: 'pointer',
              background:
                index === selectedIndex
                  ? token.colorPrimaryBg
                  : 'transparent',
              transition: 'background 0.1s',
            }}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <ChatAvatar name={item.label} src={item.avatarUrl} size={24} onTint={index === selectedIndex} />
            <span
              style={{
                fontSize: 13,
                fontWeight: index === selectedIndex ? 500 : 400,
                color: token.colorText,
              }}
            >
              {item.label}
            </span>
          </div>
        ))}
      </div>
    )
  },
)

MentionList.displayName = 'MentionList'

export function createMentionSuggestion(
  getParticipants: () => User[],
  getThemeConfig: () => { isDark: boolean; primaryColor: string },
): Partial<SuggestionOptions<MentionItem>> {
  return {
    items: ({ query }: { query: string }) => {
      const q = query.toLowerCase()
      return getParticipants()
        .filter((u) =>
          `${u.firstName} ${u.lastName}`.toLowerCase().includes(q),
        )
        .slice(0, 8)
        .map((u) => ({
          id: u.id,
          label: `${u.firstName} ${u.lastName}`,
          avatarUrl: u.avatarUrl,
        }))
    },

    render: () => {
      let popup: HTMLDivElement | null = null
      let root: ReturnType<typeof createRoot> | null = null
      let listRef: MentionListRef | null = null

      function renderPopup(props: SuggestionProps<MentionItem>) {
        if (!popup || !root) return

        const themeConfig = getThemeConfig()

        root.render(
          <ConfigProvider
            theme={{
              algorithm: themeConfig.isDark
                ? theme.darkAlgorithm
                : theme.defaultAlgorithm,
              token: { colorPrimary: themeConfig.primaryColor },
            }}
          >
            <MentionList
              ref={(el) => {
                listRef = el
              }}
              items={props.items}
              command={({ id, label }) =>
                props.command({ id, label, avatarUrl: '' })
              }
            />
          </ConfigProvider>,
        )

        // Position popup
        const rect = props.clientRect?.()
        if (rect && popup) {
          popup.style.left = `${rect.left}px`
          popup.style.top = `${rect.top - 8}px`
          popup.style.transform = 'translateY(-100%)'
        }
      }

      return {
        onStart(props: SuggestionProps<MentionItem>) {
          popup = document.createElement('div')
          popup.style.position = 'fixed'
          popup.style.zIndex = '1060'
          document.body.appendChild(popup)
          root = createRoot(popup)
          renderPopup(props)
        },

        onUpdate(props: SuggestionProps<MentionItem>) {
          renderPopup(props)
        },

        onKeyDown(props: { event: KeyboardEvent }) {
          if (props.event.key === 'Escape') {
            popup?.remove()
            root?.unmount()
            popup = null
            root = null
            return true
          }
          return listRef?.onKeyDown(props.event) ?? false
        },

        onExit() {
          root?.unmount()
          popup?.remove()
          popup = null
          root = null
          listRef = null
        },
      }
    },
  }
}
